import React, { useMemo, useState, useEffect } from 'react';
import { Button, Card, Container, Grid, Group, Input, Popover, Stack, Text, TextInput } from '@mantine/core';
import { IconCirclePlus, IconCircleX, IconEdit, IconThumbDown, IconTrash, IconWriting } from '@tabler/icons-react';
import { PersonByEmail } from '../../PersonByEmail';
import { IDynamicPerson } from '@microsoft/mgt-react';
import {
  convertStringOrUndefinedToNumber,
  createFormDataObject,
  errorBoundaryWrapper,
  parseUtcDateString, useCurrentUser, useEmployees,
  urlDefenceMarkupFix
} from '@repo/utils';
import RichTextDisplay from './RichTextDisplay';
import { truncatedNoteLength } from './config/notesConfig';
import RichText from '../../RichText';
import {AimNote, useAimContext} from '@repo/services';
import { isNotEmpty, useForm } from '@mantine/form';
import dayjs from "dayjs";

const ModalContent: ({
  id,
  triggerUpdateNote,
  triggerCreateNewNote,
  triggerDeleteNote,
  showForm,
  setShowForm,
  notes,
  isNew
}: {
  id: any;
  triggerUpdateNote: any;
  triggerCreateNewNote: any;
  triggerDeleteNote: any;
  showForm: any;
  setShowForm: any;
  notes: AimNote[];
  isNew: boolean;
}) => React.JSX.Element = ({
  id,
  triggerUpdateNote,
  triggerCreateNewNote,
  triggerDeleteNote,
  showForm,
  setShowForm,
  notes,
  isNew
}) => {
  const [noteToEdit, setNoteToEdit] = useState<AimNote | undefined>(undefined);
  const [showFullContent, setShowFullContent] = useState<boolean>(false);
  const { entityNotes, setEntityNotes, clearEntityNotes } = useAimContext();
  const { getEmployeeFromUsername, tempEmail } = useEmployees();
  const {currentUser} = useCurrentUser();

  const form = useForm({
    mode: 'uncontrolled',
    initialValues: {
      title: '',
      message: ''
    },

    validate: {
      title: isNotEmpty('Title is required'),
      message: isNotEmpty('Message is required')
    }
  } as any);

  const handleSelectNote = (noteId: string) => {
    let note;
    if (isNew) {
      note = entityNotes?.find((note) => note.aimNote === noteId);
    } else {
      note = notes?.find((note) => note.aimNote === noteId);
    }
    if (note) {
      setNoteToEdit(note);
      form.reset();
      form.setFieldValue('title', note.title);
      form.setFieldValue('message', note.message);
      setShowForm(true);
    }
  };

  const handleClearNoteToEdit = () => {
    setNoteToEdit(undefined);
    form.reset();
    setShowForm(false);
  };

  const handleSubmit = () => {
    const values = form.getValues();
    const validationResult = form.validate();
    if (!validationResult.hasErrors && id && id !== '0') {
      const payload = createFormDataObject({
        title: values.title,
        message: values.message
      });
      if (noteToEdit) {
        triggerUpdateNote({
          aimId: convertStringOrUndefinedToNumber(id, 0),
          noteId: noteToEdit.aimNote,
          data: payload
        });
      } else {
        triggerCreateNewNote({
          aimId: convertStringOrUndefinedToNumber(id, 0),
          noteId: 0,
          data: payload
        });
      }
      handleClearNoteToEdit();
    }
  };
  const handleSubmitNoteOnNewOpportunity = () => {
    const noteId = noteToEdit?.aimNote || '0';
    const vals = form.getValues();
    const validationResult = form.validate();
    if (!validationResult.hasErrors && currentUser) {
      const employee = currentUser as any;
      const val: AimNote = {
        email: employee?.mail,
        name: employee?.displayName,
        id: convertStringOrUndefinedToNumber(id, 0),
        created: dayjs().format('MM-DD-YYYY HH:mm:ss'),
        modified: dayjs().format('MM-DD-YYYY HH:mm:ss'),
        deleted: false,
        title: vals.title,
        message: vals.message,
        aimNote: noteId ?? '0',
        tags: vals.tags
      };
      const formattedTodayDate = dayjs().toISOString();
      const entityNotesCopy = [...entityNotes];
      if (noteToEdit) {
        noteToEdit.name = val.name;
        noteToEdit.title = val.title;
        noteToEdit.message = val.message;
        setEntityNotes(entityNotesCopy);
        setNoteToEdit(undefined);
      } else {
        val.aimNote = (Math.random() * 10).toFixed(4).toString();
        val.created = formattedTodayDate;
        val.modified = formattedTodayDate;
        const allNotes = [...entityNotes, val];
        setEntityNotes(allNotes);
      }
      form.setValues({title: "", message: ""})
      setShowForm(false)
    }
  }

  const handleDelete = (noteId: string) => {
    if (isNew) {
      const noteToDelete = entityNotes?.find((note) => note.aimNote === noteId);
      if (noteToDelete) {
        const index = entityNotes.indexOf(noteToDelete);
        const notesCopy = [...entityNotes];
        notesCopy.splice(index, 1);
        setEntityNotes(notesCopy);
      }
    } else {
      const noteToDelete = notes?.find((note) => note.aimNote === noteId);
      if (id && noteToDelete) {
        triggerDeleteNote({
          aimId: id,
          noteId: noteId
        });
      }
    }
  };

  const richTextVal = useMemo(() => {
    const val = form.getValues()['message'] as string;
    return val || '';
  }, [form]);

  return (
    <>
      {showForm && (
        <Card
          mt={10}
          shadow='sm'
          padding='lg'
          radius='md'
          withBorder
          style={{ position: 'sticky', top: 0, zIndex: 1000 }}
        >
          <form>
            <TextInput
              withAsterisk
              placeholder='Add a title...'
              label='Note Title'
              {...form.getInputProps('title')}
            />
            <Input.Wrapper withAsterisk label='Note' error={form.errors['message']}>
              <RichText
                content={richTextVal}
                onChange={(val: any) => {
                  form.setFieldValue('message', val);
                }}
                isError={!!form.errors['message']}
              />
            </Input.Wrapper>
            <Group p={10} gap='xs' justify='right'>
              <Button color='gray' leftSection={<IconCircleX />} onClick={handleClearNoteToEdit}>
                Close
              </Button>
              <Button leftSection={<IconCirclePlus />} onClick={isNew ? handleSubmitNoteOnNewOpportunity : handleSubmit}>
                Submit
              </Button>
            </Group>
          </form>
        </Card>
      )}
      {(isNew ? (entityNotes?.length === 0) : (notes?.length === 0)) && (
        <Container
          style={{
            textAlign: 'center',
            marginTop: '50%',
            width: '100%',
            height: window.innerHeight * 0.9
          }}
        >
          <Stack>
            {!showForm && (
              <>
                <div style={{ textAlign: 'center', width: '100%' }}>
                  <IconWriting size={60} />
                </div>
                <Text size='xl'>
                  {'No notes have been added yet. Create one by clicking "NewTransaction Note" above.'}
                </Text>
              </>
            )}
          </Stack>
        </Container>
      )}
      {(isNew ? (entityNotes?.length > 0) : (notes?.length > 0)) && (
        <Container
          style={{
            width: '100%',
            minHeight: window.innerHeight * 0.9,
            height: '100%',
            padding: 0
          }}
        >
          {(isNew ? entityNotes : notes)?.map(({ id, aimNote, ...n }) => (
            <Card key={`${id}-${aimNote}`} mt={10} shadow='sm' padding='lg' radius='md' withBorder>
              <Grid>
                <Grid.Col span={2}>
                  <PersonByEmail
                    //@ts-ignore
                    view={'avatar'}
                    avatarSize='large'
                    fallbackDetails={
                      { mail: 'Loading Team Member...', displayName: n.name } as IDynamicPerson
                    }
                    personCardInteraction={'click'}
                    email={n.email || ''}
                  />
                </Grid.Col>
                <Grid.Col>
                  <Stack gap='xs'>
                    <Text fw={700}>{n.title}</Text>
                    <Text fs='italic' size='xs'>{`Created by: ${n.name} on ${parseUtcDateString(
                      n.created
                    )}`}</Text>
                    {n.modified !== n.created && (
                      <Text fs='italic' size='xs'>{`Last modified ${parseUtcDateString(n.modified)}`}</Text>
                    )}
                    <>
                      <RichTextDisplay text={urlDefenceMarkupFix(n.message)} truncated={!showFullContent} />
                      {'   '}
                      <a
                        style={{ textDecoration: 'underline', color: 'blue', cursor: 'pointer' }}
                        onClick={() => setShowFullContent((showFullContent) => !showFullContent)}
                      >
                        {!showFullContent && n.message?.length > truncatedNoteLength
                          ? 'more'
                          : n.message?.length > truncatedNoteLength
                            ? 'less'
                            : ''}
                      </a>
                    </>
                  </Stack>
                </Grid.Col>
                <Grid.Col span={4}>
                  <Group mt={10}>
                    <IconEdit
                      color='gray'
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSelectNote(aimNote)}
                    />
                    <Popover
                      width={400}
                      withArrow
                      //@ts-ignore
                      style={{ cursor: 'pointer' }}
                    >
                      <Popover.Target>
                        <IconTrash color='gray' />
                      </Popover.Target>
                      <Popover.Dropdown>
                        <Group>
                          <Text>Are you sure you want to delete this note?</Text>
                          <Button
                            color='red'
                            mt={20}
                            rightSection={<IconThumbDown size={14} />}
                            onClick={() => handleDelete(aimNote)}
                          >
                            Delete
                          </Button>
                        </Group>
                      </Popover.Dropdown>
                    </Popover>
                  </Group>
                </Grid.Col>
              </Grid>
            </Card>
          ))}
        </Container>
      )}
    </>
  );
};

export default errorBoundaryWrapper(ModalContent);
